import { CommentAttachment, ZendeskTicketComment } from "@kuika/kuika-cl-model";
import { PureComponent } from "react";
import DateService from "../../../services/date-service";
import AttachSvg from "../../../svgs/small-icons/attach.svg";
import "./ticket-comment.scss";

interface Props {
  comment: ZendeskTicketComment;
}

interface State {
  /** A photo URL can resolve and still fail to load; falling back to the initials beats a blank disc. */
  isPhotoBroken: boolean;
}

class TicketComment extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { isPhotoBroken: false };
  }

  private onPhotoError = (): void => {
    this.setState({ isPhotoBroken: true });
  };

  private getInitialLetters = (name: string) => {
    const splitName = name.split(" ");
    const firstName = splitName.at(0);
    const lastName = splitName.at(-1);

    if (firstName && splitName.length === 1) {
      return firstName[0].toUpperCase();
    }

    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }

    return "";
  };

  onClickAttachment = (attachment: CommentAttachment) => {
    window.open(attachment.url, "_blank");
  };

  render() {
    return (
      <div className="ticket-comment">
        <div className="ticket-comment-header">
          <div className="ticket-comment-profile-container">
            {/*
              The initials are the BASE layer and the photo is overlaid on top — the same shape the
              board's avatar uses, because it survives every way a profile-photo URL can fail: absent
              (no overlay), 404/403 (onError removes it), and — the one a `src ? photo : initials`
              branch gets wrong — a URL that loads "successfully" but is blank or transparent, where
              the initials simply show through. Before this the component only SUPPRESSED the
              initials when `pic` was set and never drew an image, so a commenter who had a photo got
              an empty coloured disc.
            */}
            <div className="ticket-comment-profile-picture">
              {this.getInitialLetters(this.props.comment.author)}
              {this.props.comment.pic && !this.state.isPhotoBroken && (
                <img
                  className="ticket-comment-profile-photo"
                  src={this.props.comment.pic}
                  alt=""
                  aria-hidden
                  onError={this.onPhotoError}
                />
              )}
            </div>
            <div data-testid="profileName" className="ticket-comment-profile-name">
              {this.props.comment.author}
            </div>
          </div>
          <div data-testid="ticketMessageDate" className="ticket-comment-relative-date">
            {DateService.getHumanizedDate(this.props.comment.createdAt)}
          </div>
        </div>
        <div data-testid="ticketMessage" className="ticket-comment-message-body">
          {this.props.comment.body}
        </div>
        {this.props.comment.attachments.length > 0 && (
          <div className="ticket-comment-attachments">
            <div className="ticket-comment-attachments-prefix">
              <AttachSvg />
            </div>
            {this.props.comment.attachments.map((attachment: CommentAttachment) => (
              <button
                data-testid="ticketAttachment"
                className="ticket-comment-attachment"
                key={attachment.name}
                onClick={() => this.onClickAttachment(attachment)}
              >
                <img src={attachment.url} alt="" />
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
}

export default TicketComment;
